// SPDX-License-Identifier: Elastic-2.0
/**
 * Button — primary form primitive.
 *
 * Pill-shaped, themed via design-system tokens. Three variants
 * (primary, secondary, ghost) × three sizes (sm, md, lg). Behaves like a
 * regular HTML button — all `button` attributes pass through.
 *
 * Accessibility:
 *  - When `disabled`, both the native `disabled` attribute AND
 *    `aria-disabled` are set so assistive tech reads the state regardless
 *    of which attribute it queries.
 *  - Focus ring is peach, keyboard-visible only (`:focus-visible`),
 *    keeping mouse clicks free of distracting outlines.
 *
 * Visual contract: primary inverts ground/figure with the active theme —
 * cream-on-ink in dark, ink-on-cream in light. That's the brand
 * signature, not a bug. See WizardShell.jsx (lines 67-89) for the JSX
 * reference; this is the token-driven CSS Module port.
 */
import type { ButtonHTMLAttributes, ForwardedRef } from "react";
import { forwardRef } from "react";

import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  /** Visual variant. Defaults to `"primary"`. */
  variant?: ButtonVariant;
  /** Padding + font-size preset. Defaults to `"md"`. */
  size?: ButtonSize;
  /**
   * Native `type` attribute. We default to `"button"` (not `"submit"`)
   * because the React default of `"submit"` causes accidental form
   * submission when buttons sit inside a `<form>`.
   */
  type?: "button" | "submit" | "reset";
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: styles.primary ?? "",
  secondary: styles.secondary ?? "",
  ghost: styles.ghost ?? "",
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: styles.sizeSm ?? "",
  md: styles.sizeMd ?? "",
  lg: styles.sizeLg ?? "",
};

function ButtonImpl(
  {
    variant = "primary",
    size = "md",
    type = "button",
    disabled,
    className,
    children,
    ...rest
  }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
  const classes = [styles.btn, VARIANT_CLASS[variant], SIZE_CLASS[size], className]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      {...rest}
    >
      {children}
    </button>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(ButtonImpl);
Button.displayName = "Button";
